import {
  applyBreakPeriods,
  breakPeriodsContainer,
  notify,
  readBreakPeriods,
} from "./timesheet_voice_form"

// The timesheet form before a voice pass, so "Rückgängig" can put it back —
// rows the pass added or removed included — and so the controller can tell
// which fields the pass changed (to tag them "Sprache").
//
// Rows are captured by POSITION with names stripped of their row stamp
// (site_allocations[7][hours] -> [hours]): the row controllers re-stamp rows
// they add, so a restored row never has its old name. Von/bis pause rows carry
// a random stamp too and are captured per scope as {break_from, break_to}.

const ROW_PATTERNS = {
  site: /^site_allocations\[\d+\]/,
  entry: /^additional_entries\[\d+\]/,
}
const BREAK_PERIOD_NAME = /_break_periods_attributes\]/

// name -> current value for every named field (one entry per name): what a pass
// changed is the difference of two of these.
export function fieldValues(form) {
  const values = new Map()
  form.querySelectorAll("[name]").forEach((element) => {
    if (!isField(element) || values.has(element.name)) return
    values.set(element.name, readValue(form, element.name))
  })
  return values
}

export function changedFieldNames(before, after) {
  const changed = []
  after.forEach((value, name) => {
    if (JSON.stringify(before.get(name) ?? null) !== JSON.stringify(value)) changed.push(name)
  })
  return changed
}

// controllers: { booking, entries, nestedFormFor(scope), setSelect(select, value) }
export function snapshotForm(form, controllers) {
  const { booking, entries } = controllers
  return {
    fields: [...fieldValues(form)].filter(
      ([name]) => !rowName(name) && !BREAK_PERIOD_NAME.test(name),
    ),
    siteRows: booking
      ? booking.allocationTargets.map((row) => rowValues(row, ROW_PATTERNS.site))
      : null,
    restRow: booking?.autoAllocation
      ? booking.allocationTargets.indexOf(booking.autoAllocation)
      : -1,
    entryRows: entries
      ? entries.entryTargets.slice(1).map((block) => ({
          values: rowValues(block, ROW_PATTERNS.entry),
          breakTo: breakRowBefore(block)?.querySelector("[data-break-to]")?.value || null,
          periods: readBreakPeriods(block),
        }))
      : null,
    periods: readBreakPeriods(entries ? entries.entryTargets[0] : form),
  }
}

export function restoreForm(form, snapshot, controllers) {
  const { booking, entries, setSelect } = controllers
  if (booking && snapshot.siteRows) restoreSiteRows(booking, snapshot, setSelect)
  if (entries && snapshot.entryRows) restoreEntryRows(entries, snapshot, controllers)

  const firstScope = entries ? entries.entryTargets[0] : form
  if (breakPeriodsContainer(firstScope)) {
    applyBreakPeriods(firstScope, snapshot.periods || [], controllers.nestedFormFor(firstScope))
  }
  snapshot.fields.forEach(([name, value]) => writeValue(form, name, value, setSelect))
  entries?.chainBlocks()
  booking?.updateTotals()
}

function restoreSiteRows(booking, snapshot, setSelect) {
  while (booking.allocationTargets.length > Math.max(snapshot.siteRows.length, 1)) {
    const row = booking.allocationTargets[booking.allocationTargets.length - 1]
    const button = row.querySelector("[data-action='click->multi-site-booking#removeSite']")
    if (!button) break
    booking.removeSite({ target: button })
  }
  while (booking.allocationTargets.length < snapshot.siteRows.length) booking.addSite()
  snapshot.siteRows.forEach((values, index) => {
    writeRow(booking.allocationTargets[index], ROW_PATTERNS.site, values, setSelect)
  })
  const rest = booking.allocationTargets[snapshot.restRow]
  if (rest) booking.followRemainder(rest)
}

function restoreEntryRows(entries, snapshot, controllers) {
  entries.entryTargets.slice(1).forEach((block) => {
    const button = block.querySelector("[data-action='click->multi-entry#remove']")
    if (button) entries.remove({ target: button })
  })
  snapshot.entryRows.forEach((row) => {
    const count = entries.entryTargets.length
    if (row.breakTo && entries.hasBreakButtonTarget) {
      entries.addWithBreak()
      const breakRows = entries.containerTarget.querySelectorAll("[data-multi-entry-break-row]")
      const breakTo = breakRows[breakRows.length - 1]?.querySelector("[data-break-to]")
      if (breakTo) {
        breakTo.value = row.breakTo
        breakTo.dispatchEvent(new Event("change", { bubbles: true }))
      }
    } else {
      entries.add()
    }
    const block = entries.entryTargets[count]
    if (!block) return
    writeRow(block, ROW_PATTERNS.entry, row.values, controllers.setSelect)
    if (breakPeriodsContainer(block)) {
      applyBreakPeriods(block, row.periods || [], controllers.nestedFormFor(block))
    }
  })
}

function breakRowBefore(block) {
  const before = block.previousElementSibling
  return before?.hasAttribute("data-multi-entry-break-row") ? before : null
}

function rowName(name) {
  return ROW_PATTERNS.site.test(name) || ROW_PATTERNS.entry.test(name)
}

function rowValues(row, pattern) {
  const values = {}
  row.querySelectorAll("[name]").forEach((element) => {
    if (!isField(element) || BREAK_PERIOD_NAME.test(element.name)) return
    const suffix = element.name.replace(pattern, "")
    if (!(suffix in values)) values[suffix] = readValue(row, element.name)
  })
  return values
}

function writeRow(row, pattern, values, setSelect) {
  const prefix = row.querySelector("[name]")?.name.match(pattern)?.[0]
  if (!prefix) return
  Object.entries(values).forEach(([suffix, value]) =>
    writeValue(row, `${prefix}${suffix}`, value, setSelect),
  )
}

// Fields a user fills in: not Rails' authenticity token, not submit buttons.
function isField(element) {
  if (element.name === "authenticity_token" || element.name === "_method") return false
  if (element.tagName === "BUTTON") return false
  return !["submit", "button", "file"].includes(element.type)
}

function elementsNamed(scope, name) {
  return Array.from(scope.querySelectorAll(`[name="${CSS.escape(name)}"]`)).filter(isField)
}

// A checkbox reads as its checked state (Rails' hidden "0" companion ignored), a
// multi-select as its selected values, a radio group as the checked value.
function readValue(scope, name) {
  const elements = elementsNamed(scope, name)
  const checkbox = elements.find((element) => element.type === "checkbox")
  if (checkbox) return checkbox.checked
  const radios = elements.filter((element) => element.type === "radio")
  if (radios.length) return radios.find((radio) => radio.checked)?.value ?? null
  const element = elements[0]
  if (!element) return null
  if (element.tagName === "SELECT" && element.multiple) {
    return Array.from(element.selectedOptions).map((option) => option.value)
  }
  return element.value
}

function writeValue(scope, name, value, setSelect) {
  const elements = elementsNamed(scope, name)
  if (!elements.length) return
  if (JSON.stringify(readValue(scope, name)) === JSON.stringify(value)) return

  const checkbox = elements.find((element) => element.type === "checkbox")
  if (checkbox) {
    checkbox.checked = value === true
    notify(checkbox)
    return
  }
  const radios = elements.filter((element) => element.type === "radio")
  if (radios.length) {
    radios.forEach((radio) => {
      radio.checked = radio.value === value
    })
    const checked = radios.find((radio) => radio.checked)
    if (checked) notify(checked)
    return
  }
  const element = elements[0]
  if (element.tagName === "SELECT") {
    setSelect(element, value ?? "")
    return
  }
  element.value = value ?? ""
  notify(element)
}
